<template>
    <div class="upsell-config">
        <!-- PLAN -->
        <section id="upsell-config-plan" class="upsell-config-plan">
            <h4 class="upsell-config__heading typographies-paragraph-x-large-semibold">
                {{ $l10n.__('Plan') }}
            </h4>
            <ul class="upsell-config-plan__container">
                <li
                    v-for="plan in plans"
                    :key="plan.key"
                    :class="[
                        'upsell-config-plan__item typographies-paragraph-medium-semibold',
                        planClasses(plan.key, plan),
                    ]"
                    @click="updateSelectedPlan(plan)"
                >
                    <CompassTooltip
                        :content="
                            $t.__(
                                'pages.account.upsell.ChargebeeUpsellConfig.contact_sale_for_downgrade_plan',
                            )
                        "
                        contentClass="upsell-config-plan__item-tooltip typographies-paragraph-small-regular"
                        :disabled="plan.enabled"
                        placement="bottom-start"
                    />
                    <span class="upsell-config-plan__item-label">{{ plan.label }}</span>
                    <CompassRadio
                        :modelValue="plan.key"
                        label=" "
                        :checked="plan.key === getPlanSelected"
                        :disabled="!plan.enabled"
                        @change="updateSelectedPlan(plan)"
                    />
                </li>
            </ul>
        </section>

        <!-- LICENSES-->
        <section id="upsell-config-licenses" class="upsell-config-licenses">
            <h4 class="upsell-config__heading typographies-paragraph-x-large-semibold">
                {{ $l10n.__('Licences') }}
            </h4>
            <ul class="upsell-config-licenses__container">
                <li
                    v-for="license in licenses"
                    :key="license.id"
                    class="upsell-config-licenses__item"
                >
                    <span class="typographies-paragraph-medium-regular">
                        {{ license.label }}
                    </span>
                    <div class="upsell-config-licenses__item-price">
                        <CompassNumberInput
                            :disabled="getEstimationLoading"
                            :modelValue="getSubscriptionLicenseQuantity(license)"
                            :min="0"
                            @change="(newValue) => updateLicense(license.id, newValue)"
                        />
                        <div v-if="license.price" class="upsell-config-licenses__item-price-infos">
                            <span class="typographies-paragraph-medium-semibold">
                                {{ $filters.numberFormatCurrency(license.price / 100) }}
                            </span>
                            <span
                                v-if="license.period_unit"
                                class="typographies-paragraph-small-regular"
                            >
                                /{{ recurrencyLabel(license.period_unit) }}
                            </span>
                        </div>
                    </div>
                </li>
            </ul>
        </section>

        <!-- OPTIONS -->
        <section id="upsell-config-options" class="upsell-config-options">
            <h4 class="upsell-config__heading typographies-paragraph-x-large-semibold">
                {{ $l10n.__('Options') }}
            </h4>
            <ul class="upsell-config-options__container">
                <!-- STOCK -->
                <li v-if="isOptionEnabled('pack_stock')" class="upsell-config-options__item">
                    <div
                        class="upsell-config-options__item-infos typographies-paragraph-medium-regular"
                    >
                        <CompassSwitch
                            :disabled="isOptionEnabled('pack_stock')"
                            :modelValue="isOptionEnabled('pack_stock')"
                            @update:modelValue="(value) => updateOption('pack_stock', value)"
                        />
                        <div class="upsell-config-options__item-infos-content">
                            <span>
                                {{ options.pack_stock.label }}
                            </span>
                            <p class="upsell-config-options__item-infos-description">
                                {{
                                    $l10n.__(
                                        'Gardez une vision précise de vos stocks en temps réel',
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                    <div>
                        <span class="typographies-paragraph-medium-semibold">
                            {{ $filters.numberFormatCurrency(options.pack_stock.price / 100) }}
                        </span>
                        <span class="typographies-paragraph-small-regular">
                            /{{ recurrencyLabel(options.pack_stock.period_unit) }}
                        </span>
                    </div>
                </li>

                <!-- SUPPORT -->
                <li v-if="isOptionEnabled('pack_support')" class="upsell-config-options__item">
                    <div
                        class="upsell-config-options__item-infos typographies-paragraph-medium-regular"
                    >
                        <CompassSwitch
                            :disabled="isOptionEnabled('pack_support')"
                            :modelValue="isOptionEnabled('pack_support')"
                            @update:modelValue="(value) => updateOption('pack_support', value)"
                        />
                        <div class="upsell-config-options__item-infos-content">
                            <span>
                                {{ options.pack_support.label }}
                            </span>
                            <p class="upsell-config-options__item-infos-description">
                                {{
                                    $l10n.__(
                                        'Assistez vos clients avec une gestion de ticket collaborative',
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                    <div>
                        <span class="typographies-paragraph-medium-semibold">
                            {{ $filters.numberFormatCurrency(options.pack_support.price / 100) }}
                        </span>
                        <span class="typographies-paragraph-small-regular">
                            /{{ recurrencyLabel(options.pack_support.period_unit) }}
                        </span>
                    </div>
                </li>

                <!-- CASHFLOW -->
                <li class="upsell-config-options__item upsell-config-options__item--has-select">
                    <div
                        class="upsell-config-options__item-infos typographies-paragraph-medium-regular"
                    >
                        <CompassSwitch
                            :modelValue="isOptionEnabled('pack_cashflow')"
                            @update:modelValue="(value) => updateOption('pack_cashflow', value)"
                        />
                        <div class="upsell-config-options__item-infos-content">
                            <span>
                                {{ $l10n.__('Trésorerie') }}
                            </span>
                            <p class="upsell-config-options__item-infos-description">
                                {{
                                    $l10n.__(
                                        'Visualisez votre trésorerie en temps réel et réalisez des prévisions',
                                    )
                                }}
                            </p>
                            <CompassSelect
                                :disabled="!isOptionEnabled('pack_cashflow')"
                                :modelValue="cashflowSelectValue.id"
                                @update:modelValue="
                                    (value) =>
                                        updateLevelOption({
                                            optionNameKey: 'pack_cashflow',
                                            optionId: value,
                                        })
                                "
                            >
                                <CompassDropdownOption
                                    v-for="selectOption in options.pack_cashflow"
                                    :key="selectOption.id"
                                    :label="selectOption.level"
                                    :value="selectOption.id"
                                    :caption="getCaptionSelectPrice(selectOption)"
                                />
                            </CompassSelect>
                        </div>
                    </div>
                    <div class="upsell-config-options__item-price">
                        <span class="typographies-paragraph-medium-semibold">
                            {{
                                $filters.numberFormatCurrency(
                                    humanizeCentsPrice(cashflowSelectValue.amount),
                                )
                            }}
                        </span>
                        <span class="typographies-paragraph-small-regular">
                            &nbsp;/{{ recurrencyLabel(cashflowSelectValue.period_unit) }}
                        </span>
                    </div>
                </li>
            </ul>
        </section>
        <ChargebeeUpsellMarketing v-if="pricingVersion >= 5" id="upsell-config-marketing" />
    </div>
</template>
<script>
import ChargebeeUpsellMixin from 'mixins/chargebee/ChargebeeUpsellMixins';
import ChargebeeUpsellMarketing from 'vcomponents/pages/account/upsell/marketing/ChargebeeUpsellMarketing.vue';
import { createNamespacedHelpers } from 'vuex';
import { debounce } from 'lodash';
const { mapState: mapSubscriptionManagerState } = createNamespacedHelpers('subscriptionManager');

const chargebeeUpsellStore = createNamespacedHelpers('chargebeeUpsellStore');

const { mapState: mapCorpState } = createNamespacedHelpers('corp');

export default {
    components: {
        ChargebeeUpsellMarketing,
    },
    mixins: [ChargebeeUpsellMixin],
    data() {
        return {
            plans: [],
        };
    },
    computed: {
        ...mapSubscriptionManagerState(['versionedPlans']),
        ...chargebeeUpsellStore.mapGetters([
            'getChargebeeCatalogue',
            'getSubscriptionItems',
            'getCustomerData',
            'getDiscounts',
            'getPreviousPlan',
            'getPlanSelected',
            'getEstimationLoading',
        ]),
        ...mapCorpState(['pricingVersion']),
        cashflowSelectValue() {
            const cashflowSelectValue =
                this.getSubscriptionItems.find((option) => option.nameKey === 'pack_cashflow') ??
                this.options.pack_cashflow[0];
            cashflowSelectValue.id = cashflowSelectValue.id ?? cashflowSelectValue.item_price_id;
            cashflowSelectValue.amount = cashflowSelectValue.amount ?? cashflowSelectValue.price;
            return cashflowSelectValue;
        },
        licenses() {
            const licenses =
                this.getChargebeeCatalogue[this.getPlanSelected]?.addons?.licenses ?? [];
            for (const license of licenses) {
                let licenseExploded = license.id.split('_');
                licenseExploded = licenseExploded.slice(0, licenseExploded.length - 2);
                license.nameKey = licenseExploded.join('_');
            }
            return licenses;
        },
    },
    created() {
        const currentPlanOrder = this.versionedPlans.findIndex(
            (item) => item.code === this.getPreviousPlan,
        );
        this.plans = Object.entries(this.getChargebeeCatalogue)
            .map(([key, plan]) => {
                const order = this.versionedPlans.findIndex((item) => item.code === key);
                return {
                    label: plan.external_name,
                    enabled: order >= currentPlanOrder,
                    key,
                    order,
                };
            })
            .sort((a, b) => (a.order < b.order ? -1 : 1));
    },
    methods: {
        ...chargebeeUpsellStore.mapActions([
            'updateOptionStatus',
            'updateOptionLevel',
            'updatePlan',
            'updateAddonQuantity',
        ]),
        getCaptionSelectPrice(t) {
            return `${this.$filters.numberFormatCurrency(
                this.humanizeCentsPrice(t.price),
            )} /${this.recurrencyLabel(t.period_unit)}`;
        },
        getSubscriptionLicenseQuantity(license) {
            return this.getSubscriptionItems
                .filter((item) => item.nameKey.includes(license.nameKey))
                .reduce((sum, record) => sum + record.quantity, 0);
        },
        updateLicense: debounce(function (itemId, newValue) {
            try {
                this.updateAddonQuantity({ addonId: itemId, quantity: newValue });
            } catch {
                this.callError();
            }
        }, 300),
        updateSelectedPlan: debounce(function (newPlan) {
            if (newPlan.enabled) {
                try {
                    this.updatePlan(newPlan.key);
                } catch {
                    this.callError();
                }
            }
        }, 230),
        updateLevelOption: debounce(function ({ optionNameKey, optionId }) {
            try {
                const selectedValue = this.options[optionNameKey].find((option) => {
                    return option.id === optionId;
                });
                this.updateOptionLevel({ optionNameKey, option: selectedValue });
            } catch {
                this.callError();
            }
        }, 200),
        planClasses(key, plan) {
            return {
                'upsell-config-plan__item--is-selected': key === this.getPlanSelected,
                'upsell-config-plan__item--is-disabled': !plan.enabled,
            };
        },
    },
};
</script>
<style lang="scss">
//tooltip
.upsell-config-plan__item-tooltip {
    width: 210px;
}
</style>
<style lang="scss" scoped>
.upsell-config {
    display: grid;

    &-options,
    &-plan,
    &-licenses {
        margin-bottom: var(--spacing-5);
        padding-bottom: var(--spacing-5);
        border-bottom: 1px solid var(--colors-common-border-default);
    }

    &__heading {
        margin: 0 0 var(--spacing-1);
    }

    &-plan {
        display: flex;
        flex-direction: column;

        .upsell-config__heading {
            margin-bottom: 0;
        }
        &__container {
            display: flex;
            flex-direction: row;
            gap: var(--spacing-4);
            margin: var(--spacing-3) 0;
        }

        &__item {
            position: relative;
            display: flex;
            width: 100%;
            padding: var(--spacing-3);
            background: var(--colors-component-background-bloc-standard-default);
            border: solid 1px var(--colors-component-background-bloc-standard-default);
            border-radius: 4px;

            &::v-deep .compass-tooltip__element {
                position: absolute;
                height: 100%;
                width: 100%;
                top: 0;
                left: 0;
            }

            &:not(.upsell-config-plan__item--is-disabled):hover,
            &--is-selected {
                cursor: pointer;
                background: var(--colors-component-background-bloc-standard-hover);
            }

            &--is-selected {
                border-color: var(--colors-component-background-actionable-active-default);
            }

            &--is-disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }

            &-label {
                margin: var(--spacing-1) 0;
            }

            &::v-deep .compass-radio {
                position: absolute;
                right: var(--spacing-3);
                top: var(--spacing-3);
                input {
                    background: var(--colors-component-background-actionable-default);
                }
            }
        }
    }
    &-licenses,
    &-options {
        &__container {
            margin: 0;
            li:not(:last-child) {
                border-bottom: solid 1px var(--colors-common-border-lighter);
            }
        }

        &__item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-3) 0;

            &-price {
                display: flex;
                align-items: center;
                gap: var(--spacing-3);
                white-space: nowrap;

                &::v-deep .compass-number-input__input {
                    width: 54px;
                }
            }
        }
    }
    &-licenses__item-price-infos {
        width: 88px;
        text-align: right;
    }

    &-options__item {
        &-infos {
            display: flex;
            gap: var(--spacing-3);

            &-description {
                width: 260px;
                margin: 0 0 var(--spacing-1);
                color: var(--colors-common-text-standard-caption);
            }
        }
        &.upsell-config-options__item--has-select {
            justify-content: initial;
            gap: var(--spacing-3);

            .upsell-config-options__item-price {
                white-space: nowrap;
                gap: unset;
            }

            .upsell-config-options__item-infos {
                width: 100%;

                &-content {
                    width: 100%;

                    &::v-deep .compass-select {
                        width: 100%;
                    }
                }
            }
        }
    }
}
</style>
